import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Target, MapPin, DollarSign } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";

export default function GoogleAdsOrlandoPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Google Ads en Orlando",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Orlando",
      "addressRegion": "FL"
    },
    "description": "Gestión profesional de campañas de Google Ads en Orlando, Florida. Estrategia PPC optimizada para negocios hispanos con medición orientada a negocio."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Cuál es el presupuesto mínimo para Google Ads en Orlando?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Recomendamos mínimo $1,000-$1,500/mes en ad spend para campañas locales en Orlando. El fee de gestión se calcula por separado según el alcance del servicio."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto tiempo toma ver resultados con Google Ads?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Los anuncios empiezan a generar tráfico de inmediato. Optimización completa y datos suficientes para decisiones estratégicas toman 4-6 semanas."
        }
      },
      {
        "@type": "Question",
        "name": "¿Manejan campañas bilingües en Orlando?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, creamos campañas segmentadas por idioma (español/inglés) y por área geográfica dentro de Orlando para maximizar relevancia y conversiones."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Servicios",
        "item": "https://luisroc.com/services"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Florida",
        "item": "https://luisroc.com/servicios-locales-florida"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Google Ads Orlando",
        "item": "https://luisroc.com/google-ads-orlando"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang="es" />
        <title>Agencia Google Ads en Orlando, FL | PPC que Convierte – Luis ROC</title>
        <meta name="description" content="Campañas de Google Ads optimizadas en Orlando, Florida. Estrategia PPC bilingüe para negocios hispanos. Medición orientada a negocio y tracking de conversiones." />
        <link rel="canonical" href="https://luisroc.com/google-ads-orlando" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando, Florida" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://luisroc.com/google-ads-orlando" />
        <meta property="og:title" content="Google Ads en Orlando, FL – Luis ROC" />
        <meta property="og:description" content="Campañas PPC optimizadas para Orlando. Estrategia bilingüe y medición orientada a negocio." />
        <meta property="og:locale" content="es_US" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google Ads en Orlando, FL – Luis ROC" />
        <meta name="twitter:description" content="PPC optimizado para negocios hispanos en Orlando." />
      </Helmet>

      <Header language="es" onLanguageChange={() => {}} />
      
      <main className="min-h-screen bg-background">
        <BreadcrumbNav 
          items={[
            { name: "Servicios", url: "/servicios" },
            { name: "Florida", url: "/servicios-locales-florida" },
            { name: "Google Ads Orlando", url: "/google-ads-orlando" }
          ]}
          language="es"
        />

        <section className="bg-gradient-to-br from-primary/10 via-background to-background py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-primary" />
                <span className="text-lg font-semibold text-primary">Orlando, Florida</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Agencia Google Ads en Orlando orientada a demanda local
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Campañas PPC optimizadas para negocios hispanos en Orlando. Estrategia bilingüe, segmentación precisa y medición orientada a negocio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="whitespace-normal h-auto py-3 text-center">
                  <a href="/contacto">
                    Agenda tu reunión inicial gratuita de 30 minutos
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:+18325215050">Llama Ahora</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-foreground">
                  Qué Incluye Nuestra Gestión de Google Ads en Orlando
                </h2>
                <p className="text-lg text-muted-foreground">
                  Estrategia completa para maximizar tu inversión publicitaria en Orlando
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Auditoría de Cuenta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Análisis completo de campañas actuales: estructura, keywords, anuncios, extensiones y oportunidades de mejora.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Segmentación Geográfica</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Campañas por zona en Orlando: Downtown, Winter Park, Lake Nona, Dr. Phillips, con ajustes de pujas por área.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Estrategia Bilingüe</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Campañas separadas en español e inglés con landing pages específicas para cada audiencia en Orlando.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Optimización Continua</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Ajustes semanales de pujas, keywords negativas, A/B testing de anuncios y optimización de Quality Score.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Tracking de Conversiones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Configuración completa: llamadas, formularios, chats, con atribución por campaña y keyword.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CheckCircle className="w-8 h-8 text-primary mb-2" />
                    <CardTitle>Reportes Detallados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Informes mensuales con métricas clave: CPA, ROAS, conversiones, recomendaciones y forecast del próximo mes.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
                Preguntas Frecuentes sobre Google Ads en Orlando
              </h2>
              
              <div className="space-y-6">
                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Cuál es el presupuesto mínimo para Google Ads en Orlando?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Recomendamos mínimo $1,000-$1,500/mes en ad spend para campañas locales en Orlando. El fee de gestión se calcula por separado según el alcance del servicio.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Cuánto tiempo toma ver resultados con Google Ads?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Los anuncios empiezan a generar tráfico de inmediato. Optimización completa y datos suficientes para decisiones estratégicas toman 4-6 semanas.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Manejan campañas bilingües en Orlando?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Sí, creamos campañas segmentadas por idioma (español/inglés) y por área geográfica dentro de Orlando para maximizar relevancia y conversiones.
                  </p>
                </details>

                <details className="group bg-background rounded-lg p-6 shadow-sm">
                  <summary className="flex justify-between items-center cursor-pointer font-semibold text-lg">
                    ¿Cómo reportan el ROI de las campañas?
                    <span className="text-primary transition group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">
                    Panel mensual con: inversión total, leads generados, CPA por campaña, ROAS, y grabaciones de llamadas para validar calidad de conversiones.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <DollarSign className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-4 text-foreground">
                ¿Listo para Generar Clientes en Orlando?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Solicita una reunión inicial gratuita de tu cuenta de Google Ads y descubre cómo optimizar tu inversión.
              </p>
              <Button asChild size="lg" className="text-lg whitespace-normal h-auto py-3 text-center">
                <a href="/contacto">
                  Agenda tu reunión inicial gratuita de 30 minutos
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

        <KnowledgeConnections path="/google-ads-orlando" language="es" />

      <Footer language="es" />
      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
    </>
  );
}
